import React, { useState, useEffect } from "react";
import { Card, Modal, Button, Row, Col } from "react-bootstrap";
import {
  FiInfo,
  FiX,
  FiCalendar,
  FiBarChart2,
  FiTrendingUp,
  FiFilter,
  FiDollarSign,
  FiMaximize2,
} from "react-icons/fi";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";

const NOMI_MESI = [
  "Gen",
  "Feb",
  "Mar",
  "Apr",
  "Mag",
  "Giu",
  "Lug",
  "Ago",
  "Set",
  "Ott",
  "Nov",
  "Dic",
];

// Restituisce la data locale in formato YYYY-MM-DD senza rischi di timezone
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const SalesChart = () => {
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [vista, setVista] = useState("giorno");

  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    axios
      .get("./ordini_urgenti.json")
      .then((res) => {
        const righeRaw = res.data?.CROSS_DOCKING || [];

        /*
         * FILTRO DATI
         *
         * Vengono escluse le righe dove:
         * qta_dev = 0
         * E
         * qta_eva < quantita
         */
        const righe = righeRaw.filter((item) => {
          const qtaDev = parseFloat(item.qta_dev) || 0;
          const qtaEva = parseFloat(item.qta_eva) || 0;
          const quantita = parseFloat(item.quantita) || 0;

          return !(qtaDev === 0 && qtaEva < quantita);
        });

        const oggi = new Date();

        /*
         * Stringhe locali YYYY-MM-DD.
         * Evitiamo la conversione UTC per non spostare accidentalmente il giorno.
         */
        const oggiStr = toLocalDateStr(oggi);

        const trentaGiorniFa = new Date(oggi);
        trentaGiorniFa.setDate(trentaGiorniFa.getDate() - 30);

        const trentaGiorniFaStr = toLocalDateStr(trentaGiorniFa);

        const annoCorrente = oggi.getFullYear();
        const meseCorrente = oggi.getMonth();

        /*
         * ================================
         * VISTA GIORNO
         * ================================
         *
         * Ultimi 30 giorni fino a oggi.
         */
        const mappaGiorni = {};

        righe.forEach((item) => {
          const dataStr = item.data_doc
            ? String(item.data_doc).split(" ")[0]
            : null;

          if (!dataStr) return;

          /*
           * Confronto lessicografico su YYYY-MM-DD:
           * evita problemi di timezone.
           */
          if (dataStr < trentaGiorniFaStr || dataStr > oggiStr) return;

          const importo = parseFloat(item["Importo for"]) || 0;

          mappaGiorni[dataStr] =
            (mappaGiorni[dataStr] || 0) + importo;
        });

        const arrayGiorni = Object.keys(mappaGiorni)
          .sort()
          .map((key) => {
            const [, mese, giorno] = key.split("-");

            return {
              data: key,
              etichetta: `${giorno}/${mese}`,
              valore:
                Math.round(mappaGiorni[key] * 100) / 100,
            };
          });

        setDailyData(arrayGiorni);

        /*
         * ================================
         * VISTA ANNO
         * ================================
         *
         * Da gennaio al mese corrente
         * dell'anno corrente.
         */
        const mappaMesi = {};

        righe.forEach((item) => {
          const dataStr = item.data_doc
            ? String(item.data_doc).split(" ")[0]
            : null;

          if (!dataStr) return;

          const annoItem = parseInt(dataStr.split("-")[0]);
          const meseItem =
            parseInt(dataStr.split("-")[1]) - 1;

          if (annoItem !== annoCorrente) return;
          if (meseItem > meseCorrente) return;

          const importo =
            parseFloat(item["Importo for"]) || 0;

          mappaMesi[meseItem] =
            (mappaMesi[meseItem] || 0) + importo;
        });

        const arrayMesi = [];

        for (let m = 0; m <= meseCorrente; m++) {
          arrayMesi.push({
            mese: NOMI_MESI[m],
            valore:
              Math.round(
                (mappaMesi[m] || 0) * 100,
              ) / 100,
          });
        }

        setMonthlyData(arrayMesi);
      })
      .catch((err) =>
        console.error(
          "Errore caricamento Cross Docking Mese/Anno:",
          err,
        ),
      );
  }, []);

  const formatEuro = (val) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(val);

  /*
   * Limita il numero di etichette visibili sull'asse X
   * quando ci sono molti giorni.
   */
  const xAxisInterval = Math.max(
    0,
    Math.ceil(dailyData.length / 12) - 1,
  );

  const renderChartContent = (height = 250) => (
    <div
      style={{
        width: "100%",
        height,
      }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
      >
        {vista === "giorno" ? (
          /*
           * ===================================
           * VISTA ULTIMI 30 GIORNI
           * ===================================
           */
          <AreaChart
            data={dailyData}
            margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient
                id="colorValue"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor="#1f1f1f"
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor="#000000"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#eee"
            />

            <XAxis
              dataKey="etichetta"
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#999",
                fontSize: 11,
              }}
              dy={10}
              interval={xAxisInterval}
            />

            <YAxis
              tickFormatter={formatEuro}
              width={80}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#999",
                fontSize: 11,
              }}
            />

            <ChartTooltip
              contentStyle={{
                borderRadius: "10px",
                border: "none",
                boxShadow:
                  "0 4px 12px rgba(0,0,0,0.1)",
                fontSize: "13px",
              }}
              labelFormatter={(_, payload) =>
                payload?.[0]?.payload?.data || ""
              }
              formatter={(value) => [
                formatEuro(value),
                "Importo Fornitore",
              ]}
              cursor={{
                stroke: "#b81f25",
                strokeWidth: 1,
                strokeDasharray: "5 5",
              }}
            />

            <Area
              type="monotone"
              dataKey="valore"
              stroke="#b81f25"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorValue)"
              animationBegin={0}
              animationDuration={1200}
            />
          </AreaChart>
        ) : (
          /*
           * ===================================
           * VISTA ANNO CORRENTE
           * ===================================
           */
          <BarChart
            data={monthlyData}
            margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#eee"
            />

            <XAxis
              dataKey="mese"
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#999",
                fontSize: 11,
              }}
              dy={10}
            />

            <YAxis
              tickFormatter={formatEuro}
              width={80}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#999",
                fontSize: 11,
              }}
            />

            <ChartTooltip
              contentStyle={{
                borderRadius: "10px",
                border: "none",
                boxShadow:
                  "0 4px 12px rgba(0,0,0,0.1)",
                fontSize: "13px",
              }}
              formatter={(value) => [
                formatEuro(value),
                "Importo Fornitore",
              ]}
              cursor={{
                fill: "rgba(184,31,37,0.08)",
              }}
            />

            <Bar
              dataKey="valore"
              fill="#b81f25"
              radius={[6, 6, 0, 0]}
              animationDuration={1000}
            />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );

  return (
    <>
      <Card
        className="card card-home graphic-day hover-effect border-0"
        style={{
          borderRadius: "10px",
          padding: "1rem",
        }}
      >
        {/* ============================
            HEADER WIDGET
        ============================ */}
        <div className="d-flex justify-content-between align-items-center mb-4 px-3">
          <div className="d-flex align-items-center">
            <span
              onClick={() =>
                setShowFullscreenModal(true)
              }
              style={{
                fontWeight: "800",
                fontSize: "0.75rem",
                color: "#1a1a1a",
                letterSpacing: "1px",
                marginRight: "8px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              CROSS DOCKING MESE/ANNO
            </span>

            {/* INFO DETTAGLIATA */}
            <button
              type="button"
              className="cross-info-btn ms-1"
              onClick={() =>
                setShowInfoModal(true)
              }
              aria-label="Informazioni sul grafico Cross Docking"
            >
              <FiInfo size={14} />
            </button>
          </div>

          {/* CAMBIO VISTA */}
          <button
            type="button"
            className="badge rounded-pill bg-light text-dark border py-2 px-3"
            onClick={() =>
              setVista(
                vista === "giorno"
                  ? "anno"
                  : "giorno",
              )
            }
            style={{
              cursor: "pointer",
              fontSize: "0.65rem",
              fontWeight: "600",
            }}
          >
            {vista === "giorno"
              ? "Visualizza anno"
              : "Visualizza mese"}
          </button>
        </div>

        {renderChartContent(250)}
      </Card>

      {/* ==========================================================
          MODALE INFORMAZIONI
      ========================================================== */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
        scrollable
        className="p-5"
        style={{
          zIndex: 21000,
        }}
        backdropClassName="cross-info-backdrop"
      >
        {/* HEADER */}
        <Modal.Header
          closeButton
          className="border-bottom bg-white px-4 py-3"
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
              style={{
                width: "40px",
                height: "40px",
              }}
            >
              <FiBarChart2 size={20} />
            </div>

            <div>
              <Modal.Title
                className="fw-bold"
                style={{
                  fontSize: "1.05rem",
                }}
              >
                Come leggere Cross Docking
                Mese/Anno
              </Modal.Title>

              <div
                className="text-muted mt-1"
                style={{
                  fontSize: "0.78rem",
                }}
              >
                Guida al monitoraggio
                economico degli acquisti
                fornitore nel flusso Cross
                Docking.
              </div>
            </div>
          </div>
        </Modal.Header>

        {/* BODY */}
        <Modal.Body className="bg-light p-4">
          {/* INTRODUZIONE */}
          <div className="bg-primary-subtle border border-primary-subtle rounded-4 p-3 mb-3">
            <div className="fw-bold text-primary mb-1">
              A cosa serve
            </div>

            <div
              className="text-dark"
              style={{
                fontSize: "0.84rem",
                lineHeight: 1.55,
              }}
            >
              Il grafico mostra l'andamento
              economico degli ordini
              fornitore collegati ai flussi di{" "}
              <strong>Cross Docking</strong>.
              L'obiettivo è visualizzare
              rapidamente quanto valore di
              acquisto viene movimentato nel
              tempo, sia con un dettaglio
              giornaliero recente sia con una
              sintesi mensile dell'anno
              corrente.
            </div>
          </div>

          {/* ==========================================
              1 - FONTE DATI
          ========================================== */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">
                    1
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Quali dati vengono
                      analizzati
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Fonte e valore economico
                    </div>
                  </div>
                </div>

                <ul className="cross-info-list">
                  <li>
                    I dati provengono dalla
                    sezione{" "}
                    <strong>
                      CROSS_DOCKING
                    </strong>{" "}
                    del flusso ordini.
                  </li>

                  <li>
                    Per ogni riga viene
                    utilizzato il campo{" "}
                    <strong>
                      Importo for
                    </strong>
                    .
                  </li>

                  <li>
                    Il valore rappresentato è
                    quindi un{" "}
                    <strong>
                      importo fornitore
                    </strong>
                    , cioè il valore economico
                    di acquisto.
                  </li>

                  <li>
                    Gli importi delle righe
                    appartenenti allo stesso
                    giorno o allo stesso mese
                    vengono sommati.
                  </li>
                </ul>
              </div>
            </Col>

            {/* ==========================================
                2 - FILTRO
            ========================================== */}
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">
                    2
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Filtro sulle righe
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Quali record vengono
                      esclusi
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-3 align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle text-warning flex-shrink-0"
                    style={{
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <FiFilter size={16} />
                  </div>

                  <div
                    style={{
                      fontSize: "0.82rem",
                      lineHeight: 1.55,
                    }}
                  >
                    Prima di costruire il
                    grafico il sistema
                    esclude le righe in cui{" "}
                    <strong>
                      Q.tà Da Evadere = 0
                    </strong>{" "}
                    e contemporaneamente la{" "}
                    <strong>
                      Q.tà Evasa è inferiore
                      alla Q.tà Ordinata
                    </strong>
                    .
                  </div>
                </div>

                <div
                  className="mt-3 p-3 rounded-3 bg-light border"
                  style={{
                    fontSize: "0.78rem",
                  }}
                >
                  Questo filtro viene
                  applicato prima sia della
                  vista giornaliera sia della
                  vista annuale.
                </div>
              </div>
            </Col>
          </Row>

          {/* ==========================================
              3 - VISTA 30 GIORNI
          ========================================== */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">
                    3
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Vista Mese
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Andamento degli ultimi
                      30 giorni
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-3 align-items-start mb-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle text-danger flex-shrink-0"
                    style={{
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <FiCalendar size={16} />
                  </div>

                  <div
                    style={{
                      fontSize: "0.82rem",
                      lineHeight: 1.55,
                    }}
                  >
                    La vista chiamata{" "}
                    <strong>
                      “Mese”
                    </strong>{" "}
                    non rappresenta il mese
                    solare corrente: mostra
                    una finestra mobile degli{" "}
                    <strong>
                      ultimi 30 giorni fino a
                      oggi
                    </strong>
                    .
                  </div>
                </div>

                <ul className="cross-info-list">
                  <li>
                    Ogni punto rappresenta un
                    giorno.
                  </li>

                  <li>
                    Tutti gli importi
                    fornitore dello stesso
                    giorno vengono sommati.
                  </li>

                  <li>
                    L'asse orizzontale mostra
                    giorno e mese.
                  </li>

                  <li>
                    Passando con il mouse sul
                    grafico viene mostrata la
                    data completa e l'importo
                    del giorno.
                  </li>
                </ul>
              </div>
            </Col>

            {/* ==========================================
                4 - VISTA ANNO
            ========================================== */}
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">
                    4
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Vista Anno
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Progressivo mensile
                      dell'anno corrente
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-3 align-items-start mb-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-success-subtle text-success flex-shrink-0"
                    style={{
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <FiTrendingUp size={16} />
                  </div>

                  <div
                    style={{
                      fontSize: "0.82rem",
                      lineHeight: 1.55,
                    }}
                  >
                    La vista annuale mostra
                    gli importi aggregati per
                    mese a partire da{" "}
                    <strong>gennaio</strong>{" "}
                    fino al{" "}
                    <strong>
                      mese corrente
                    </strong>
                    .
                  </div>
                </div>

                <ul className="cross-info-list">
                  <li>
                    Ogni barra rappresenta un
                    mese.
                  </li>

                  <li>
                    Vengono considerate solo
                    righe appartenenti
                    all'anno corrente.
                  </li>

                  <li>
                    I mesi futuri non vengono
                    visualizzati.
                  </li>

                  <li>
                    Un mese senza importi
                    viene comunque mostrato
                    con valore zero.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* ==========================================
              5 - DATE
          ========================================== */}
          <div className="cross-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cross-info-step">
                5
              </span>

              <div>
                <div className="fw-bold text-dark">
                  Come vengono gestite le date
                </div>

                <div
                  className="text-muted"
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Confronto locale senza
                  spostamenti di giorno
                </div>
              </div>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div
                  style={{
                    fontSize: "0.82rem",
                    lineHeight: 1.55,
                  }}
                >
                  Per ogni riga viene
                  utilizzata{" "}
                  <strong>
                    data_doc
                  </strong>
                  . Dal valore viene preso
                  solamente il giorno in
                  formato{" "}
                  <strong>
                    AAAA-MM-GG
                  </strong>
                  , ignorando l'orario.
                </div>
              </Col>

              <Col md={6}>
                <div
                  style={{
                    fontSize: "0.82rem",
                    lineHeight: 1.55,
                  }}
                >
                  Il confronto viene fatto
                  tramite stringhe di data
                  locale anziché convertire
                  tutto in UTC. Questo evita
                  che un ordine possa essere
                  attribuito per errore al
                  giorno precedente o
                  successivo a causa del fuso
                  orario.
                </div>
              </Col>
            </Row>
          </div>

          {/* ==========================================
              6 - COME LEGGERE
          ========================================== */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">
                    6
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Come leggere i valori
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Interpretazione
                      economica
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-3 align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
                    style={{
                      width: "36px",
                      height: "36px",
                    }}
                  >
                    <FiDollarSign size={16} />
                  </div>

                  <div
                    style={{
                      fontSize: "0.82rem",
                      lineHeight: 1.55,
                    }}
                  >
                    Un valore più alto
                    significa che nel periodo
                    considerato è stato
                    movimentato un maggiore{" "}
                    <strong>
                      valore di acquisto
                      fornitore
                    </strong>{" "}
                    attraverso il Cross
                    Docking.
                  </div>
                </div>

                <div
                  className="mt-3 text-muted"
                  style={{
                    fontSize: "0.78rem",
                  }}
                >
                  Il grafico non rappresenta
                  quantità fisiche, numero di
                  ordini o valore di vendita
                  cliente: rappresenta
                  esclusivamente la somma
                  dell'importo fornitore.
                </div>
              </div>
            </Col>

            {/* ==========================================
                7 - NAVIGAZIONE
            ========================================== */}
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">
                    7
                  </span>

                  <div>
                    <div className="fw-bold text-dark">
                      Navigazione
                    </div>

                    <div
                      className="text-muted"
                      style={{
                        fontSize:
                          "0.75rem",
                      }}
                    >
                      Cambio vista e
                      fullscreen
                    </div>
                  </div>
                </div>

                <ul className="cross-info-list">
                  <li>
                    Usa{" "}
                    <strong>
                      Visualizza anno
                    </strong>{" "}
                    per passare dalla vista
                    degli ultimi 30 giorni al
                    riepilogo mensile.
                  </li>

                  <li>
                    Usa{" "}
                    <strong>
                      Visualizza mese
                    </strong>{" "}
                    per tornare alla vista
                    giornaliera.
                  </li>

                  <li>
                    Clicca sul titolo{" "}
                    <strong>
                      CROSS DOCKING MESE/ANNO
                    </strong>{" "}
                    per aprire il grafico a
                    schermo intero.
                  </li>

                  <li>
                    La stessa vista selezionata
                    viene mantenuta anche nel
                    fullscreen.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* ==========================================
              8 - ESEMPIO
          ========================================== */}
          <div className="cross-info-card">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cross-info-step">
                8
              </span>

              <div>
                <div className="fw-bold text-dark">
                  Esempio pratico
                </div>

                <div
                  className="text-muted"
                  style={{
                    fontSize: "0.75rem",
                  }}
                >
                  Come viene costruito il
                  valore visualizzato
                </div>
              </div>
            </div>

            <div
              className="p-3 rounded-4 border bg-light"
              style={{
                fontSize: "0.82rem",
                lineHeight: 1.55,
              }}
            >
              Se nello stesso giorno sono
              presenti tre righe Cross
              Docking con importi fornitore di{" "}
              <strong>€ 1.000</strong>,{" "}
              <strong>€ 2.500</strong> e{" "}
              <strong>€ 500</strong>, il
              punto del grafico relativo a
              quel giorno mostrerà{" "}
              <strong>€ 4.000</strong>.
              Nella vista annuale, quel valore
              sarà sommato a tutti gli altri
              importi appartenenti allo stesso
              mese.
            </div>
          </div>
        </Modal.Body>

        {/* FOOTER */}
        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
          <div
            className="text-muted"
            style={{
              fontSize: "0.75rem",
            }}
          >
            Suggerimento: usa la vista Mese
            per leggere l'andamento recente e
            la vista Anno per confrontare
            l'evoluzione mensile.
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="light"
              onClick={() =>
                setShowInfoModal(false)
              }
            >
              Chiudi
            </Button>

            <Button
              variant="primary"
              onClick={() => {
                setShowInfoModal(false);
                setShowFullscreenModal(true);
              }}
            >
              <FiMaximize2
                size={15}
                className="me-1"
              />
              Apri vista estesa
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ==========================================================
          MODALE FULLSCREEN
      ========================================================== */}
      <Modal
        show={showFullscreenModal}
        onHide={() =>
          setShowFullscreenModal(false)
        }
        fullscreen
        animation
      >
        <Modal.Body
          className="p-0 d-flex flex-column"
          style={{
            background: "#f8f9fa",
          }}
        >
          {/* HEADER FULLSCREEN */}
          <div
            className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm"
            style={{
              zIndex: 110,
            }}
          >
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontWeight: "800",
                  fontSize: "0.9rem",
                  color: "#1a1a1a",
                  letterSpacing: "1px",
                  marginRight: "8px",
                }}
              >
                CROSS DOCKING MESE/ANNO
              </span>

              {/* INFO DISPONIBILE ANCHE IN FULLSCREEN */}
              <button
                type="button"
                className="cross-info-btn ms-1"
                onClick={() =>
                  setShowInfoModal(true)
                }
                aria-label="Informazioni sul grafico Cross Docking"
              >
                <FiInfo size={16} />
              </button>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="badge rounded-pill bg-light text-dark border py-2 px-3"
                onClick={() =>
                  setVista(
                    vista === "giorno"
                      ? "anno"
                      : "giorno",
                  )
                }
                style={{
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                }}
              >
                {vista === "giorno"
                  ? "Visualizza anno"
                  : "Visualizza mese"}
              </button>

              <Button
                variant="outline-dark"
                className="d-flex align-items-center gap-1 border-0"
                onClick={() =>
                  setShowFullscreenModal(
                    false,
                  )
                }
                style={{
                  borderRadius: "10px",
                  height: "38px",
                }}
              >
                <FiX size={16} />
                Chiudi
              </Button>
            </div>
          </div>

          {/* GRAFICO FULLSCREEN */}
          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center">
            <div className="bg-white p-4 shadow-sm rounded-4 border w-100 h-100 d-flex align-items-center">
              {renderChartContent("80%")}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* ==========================================================
          STILI MODALE INFO
      ========================================================== */}
      <style>{`
        .cross-info-btn {
          width: 27px;
          height: 27px;
          border: 1px solid #dfe3e8;
          border-radius: 50%;
          background: #fff;
          color: #0d6efd;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: all 0.15s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          cursor: pointer;
        }

        .cross-info-btn:hover {
          background: #e7f1ff;
          border-color: #b6d4fe;
          transform: translateY(-1px);
        }

        .cross-info-backdrop {
          z-index: 20990 !important;
        }

        .cross-info-card {
          border: 1px solid #e9ecef;
          border-radius: 14px;
          background: #fff;
          padding: 16px;
          height: 100%;
        }

        .cross-info-step {
          width: 28px;
          height: 28px;
          min-width: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.72rem;
          font-weight: 800;
          background: #e7f1ff;
          color: #0d6efd;
        }

        .cross-info-list {
          margin: 0;
          padding-left: 18px;
          color: #495057;
          font-size: 0.82rem;
        }

        .cross-info-list li + li {
          margin-top: 7px;
        }
      `}</style>
    </>
  );
};

export default SalesChart;